import { useMemo } from "react";
import { cn } from "../ui/cn";
import { Mascot } from "../ui/Mascot";
import { Bubble } from "./Bubble";
import { ChatComposer } from "./ChatComposer";
import { ChatHeader } from "./ChatHeader";
import { TypingDots } from "./TypingDots";
import { groupMessages } from "./groupMessages";
import { useTypewriter } from "./useTypewriter";
import type { ChatMessage } from "./types";

export type { ChatMessage } from "./types";

type ChatThreadProps = {
  messages: ChatMessage[];
  loop?: boolean;
  height?: number | string;
  minHeight?: number | string;
  botName?: string;
  mascot?: boolean;
  loopRestartMs?: number;
  className?: string;
};

export function ChatThread({
  messages,
  loop = true,
  height,
  minHeight,
  botName = "Sidestack",
  mascot = false,
  loopRestartMs,
  className,
}: ChatThreadProps) {
  const { step, typing } = useTypewriter(messages, loop, loopRestartMs);
  const visible = useMemo(
    () => groupMessages(messages.slice(0, step)),
    [messages, step],
  );

  // Default to a fixed height to prevent layout shifts as messages stream in.
  const resolvedHeight = height ?? (minHeight ? undefined : 360);

  return (
    <div
      className={cn(
        "relative flex flex-col overflow-hidden rounded-lg",
        "bg-tg-window border border-dashed border-dash-d",
        "shadow-[0_30px_80px_rgba(0,0,0,0.4)]",
        "font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]",
        className,
      )}
      style={{
        height: resolvedHeight,
        minHeight: minHeight,
      }}
    >
      <ChatHeader botName={botName} />
      <div
        className="flex flex-col justify-end flex-1 min-h-0 gap-0.5 px-3.5 pt-3.5 pb-2.5 overflow-hidden bg-[#0E1621]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(14, 22, 33, 0.55), rgba(14, 22, 33, 0.55)), url('/telegram_bg.jpg')",
          backgroundSize: "cover, 420px auto",
          backgroundPosition: "center, center",
          backgroundRepeat: "no-repeat, repeat",
        }}
      >
        {visible.map((m, i) => (
          <Bubble key={i} m={m} />
        ))}
        {typing && <TypingDots />}
      </div>
      <ChatComposer />
      {mascot && (
        <div className="absolute bottom-[60px] right-3.5 opacity-90">
          <Mascot size={32} />
        </div>
      )}
    </div>
  );
}
